import React, { useContext } from 'react';
import { authContext } from '../../context/AuthContext';
import { SidebarContext } from '../../context/SidebarContext';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';

// Icons
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';

export default function MainApp() {
    const { isOpen } = useContext(SidebarContext)
    const { userRole } = useContext(authContext);
    const location = useLocation();

    const pages = [
        {
            icon: <ReceiptLongOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Invoicing',
            link: '/invoice-file-upload',
            render: true,
            subFolder: [
                { name: 'File Upload', link: '/invoice-file-upload' },
                { name: 'Invoices', link: '/invoices' },
            ],
        },
        {
            icon: <CategoryOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Catalog',
            link: '/products',
            render: true,
            subFolder: [
                { name: 'Products', link: '/products' },
                { name: 'Buyer View', link: '/buyer-view' },
                { name: 'Catalog Items', link: '/catalog-items' },
                { name: 'Upload Products', link: '/upload-inventory' },
            ],
        },
        {
            icon: <StyleOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Attributes',
            link: '/catalog/brands',
            render: true,
            subFolder: [
                { name: 'Brands', link: '/catalog/brands' },
                { name: 'Sizes', link: '/catalog/sizes' },
                { name: 'Colors', link: '/catalog/colors' },
                { name: 'Models', link: '/catalog/models' },
            ],
        },
        {
            icon: <InventoryOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Warehouse Shelves',
            link: '/shelf-inventory',
            render: true,
            subFolder: [
                { name: 'Shelf Inventory', link: '/shelf-inventory' },
                { name: 'Shelf Configs', link: '/shelf-configs' },
                { name: 'Box Packing', link: '/catalog/box-packing' },
            ],
        },
        {
            icon: <LocalShippingOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Orders + Shipping',
            link: '/orders',
            render: true,
            subFolder: [
                { name: 'Orders', link: '/orders' },
                { name: 'Fulfillment', link: '/fulfillment' },
                { name: 'Shipping', link: '/order-shipping' },
                { name: 'Returns', link: '/returns' },
            ],
        },
        {
            icon: <FactCheckOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Inspections',
            link: '/inspections',
            render: true,
            subFolder: [
                { name: 'Inspection Wizard', link: '/inspections/wizard' },
                { name: 'Tree Builder', link: '/inspections/trees' },
                { name: 'Status Checks', link: '/catalog/status-checks' },
            ],
        },
        {
            icon: <AssessmentOutlinedIcon sx={{ fontSize: '22px' }} />,
            name: 'Reporting',
            link: '/reporting',
            render: true,
            subFolder: [
                { name: 'Orders', link: '/reporting/orders' },
                { name: 'Inventory', link: '/reporting/inventory' },
                { name: 'Purchasing', link: '/reporting/purchasing' },
                { name: 'Research', link: '/reporting/research' },
            ],
        },
    ];

    const menuItemStyles = {
        button: ({ active, level }) => ({
            backgroundColor: active ? 'rgba(13, 148, 136, 0.12)' : 'transparent',
            color: active ? '#0F766E' : '#1C2430',
            fontWeight: active ? 700 : 600,
            borderRadius: 10,
            paddingLeft: level > 0 ? '14px !important' : undefined,
            '&:hover': {
                backgroundColor: active
                    ? 'rgba(13, 148, 136, 0.16)'
                    : 'rgba(71, 85, 105, 0.08)',
            },
        }),
        subMenuContent: ({ collapsed }) => ({
            backgroundColor: '#F1F5F9',
            zIndex: collapsed && '10000',
        }),
        label: {
            fontWeight: 'inherit',
        },
        SubMenuExpandIcon: {
            display: 'flex',
            alignItems: 'center',
        },
        root: ({ collapsed }) => {
            if (collapsed) {
                return {
                    marginBottom: '6px',
                    display: 'flex',
                    justifyContent: 'center',
                };
            }
            return { marginBottom: 2 };
        },
    };

    return (
        <Sidebar
            collapsed={!isOpen}
            backgroundColor="#F1F5F9"
            rootStyles={{
                paddingTop: '8px',
                width: '220px',
                height: '100%',
                border: 'none',
                borderRight: '1px solid #E2E8F0',
                fontSize: '14px',
                color: '#1C2430',
                ['.ps-sidebar-container']: {
                    background: '#F1F5F9 !important',
                    backgroundColor: '#F1F5F9 !important',
                    overflowY: 'hidden',
                },
                ['.ps-menu-button']: {
                    height: '42px !important',
                    margin: '2px 10px',
                    borderRadius: '10px',
                    paddingLeft: '12px !important',
                    paddingRight: '12px !important',
                },
                ['.ps-submenu-content']: {
                    backgroundColor: '#F1F5F9 !important',
                },
                ['.ps-submenu-content .ps-menu-button']: {
                    height: '38px !important',
                    marginLeft: '18px',
                    marginRight: '10px',
                    fontSize: '13px',
                },
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#F1F5F9' }}>
                <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                    <Menu
                        menuItemStyles={menuItemStyles}
                        renderExpandIcon={({ open }) => (
                            <KeyboardArrowRightRoundedIcon
                                sx={{
                                    fontSize: 22,
                                    color: '#5C6570',
                                    transition: 'transform 0.2s ease',
                                    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            />
                        )}
                    >
                        {pages
                            .filter(pg => pg.render)
                            .map((pg) => {
                                const isParentActive = location.pathname.startsWith(pg.link);
                                if (pg.subFolder.length > 0) {
                                    return (
                                        <SubMenu
                                            key={pg.name}
                                            label={pg.name}
                                            icon={pg.icon}
                                            defaultOpen={pg.subFolder.some(sub =>
                                                location.pathname === sub.link ||
                                                location.pathname.startsWith(`${sub.link}/`)
                                            )}
                                        >
                                            {pg.subFolder.map((sub) => (
                                                <MenuItem
                                                    key={sub.link}
                                                    active={
                                                        location.pathname === sub.link ||
                                                        location.pathname.startsWith(`${sub.link}/`)
                                                    }
                                                    component={<Link to={sub.link} />}
                                                    icon={
                                                        <SubdirectoryArrowRightRoundedIcon
                                                            sx={{ fontSize: 18, color: '#9AA3AD' }}
                                                        />
                                                    }
                                                >
                                                    {sub.name}
                                                </MenuItem>
                                            ))}
                                        </SubMenu>
                                    );
                                }

                                return (
                                    <MenuItem
                                        key={pg.link}
                                        active={isParentActive}
                                        component={<Link to={pg.link} />}
                                        icon={pg.icon}
                                    >
                                        {pg.name}
                                    </MenuItem>
                                );
                            })}
                    </Menu>
                </div>

                <Menu menuItemStyles={menuItemStyles} style={{ flexShrink: 0 }}>
                    <hr style={{ margin: '8px 20px', border: '0', borderTop: '1px solid #E2E8F0' }} />
                    <MenuItem
                        active={location.pathname.startsWith('/configuration')}
                        component={<Link to="/configuration" />}
                        icon={<TuneOutlinedIcon sx={{ fontSize: '22px' }} />}
                    >
                        Configuration
                    </MenuItem>
                    {userRole === "GlobalAdmin" && (
                        <MenuItem
                            active={location.pathname === '/manage-account'}
                            component={<Link to="/manage-account" />}
                            icon={<SettingsOutlinedIcon sx={{ fontSize: '22px' }} />}
                        >
                            Manage
                        </MenuItem>
                    )}
                </Menu>
            </div>
        </Sidebar >
    );
}
